import { IntegrationKey, type IIntegrationKey, type IntegrationsStore } from 'entities/Integrations'
import type {
  IntegrationFieldLoader,
  IntegrationFieldLoaderRegistry,
} from '../types/IntegrationFieldLoader'
import { SalesforceFieldLoader } from './SalesforceFieldLoader'
import { GenericFieldLoader } from './GenericFieldLoader'

export class FieldLoaderRegistryImpl implements IntegrationFieldLoaderRegistry {
  private loaders: Map<IIntegrationKey, IntegrationFieldLoader> = new Map()

  constructor(private _integrationsStore: IntegrationsStore) {
    this.initializeDefaultLoaders()
  }

  private initializeDefaultLoaders() {
    // Register Salesforce with custom loader
    this.loaders.set(IntegrationKey.salesforce, new SalesforceFieldLoader(this._integrationsStore))

    // Register other integrations with generic loader
    const genericIntegrations: IIntegrationKey[] = [
      IntegrationKey.hubspot,
      IntegrationKey.activecampaign,
      IntegrationKey.infusionsoft,
      IntegrationKey.pipedrive,
      IntegrationKey.salesmessage,
    ]

    genericIntegrations.forEach((key) => {
      this.loaders.set(key, new GenericFieldLoader(key))
    })
  }

  getLoader(integrationKey: IIntegrationKey): IntegrationFieldLoader | undefined {
    return this.loaders.get(integrationKey)
  }

  registerLoader(integrationKey: IIntegrationKey, loader: IntegrationFieldLoader): void {
    this.loaders.set(integrationKey, loader)
  }

  hasCustomLoader(integrationKey: IIntegrationKey): boolean {
    const loader = this.loaders.get(integrationKey)
    return loader ? !loader.useGenericApi() : false
  }

  getGenericIntegrations(): IIntegrationKey[] {
    return Array.from(this.loaders.entries())
      .filter(([_, loader]) => loader.useGenericApi())
      .map(([key, _]) => key)
  }

  getCustomIntegrations(): IIntegrationKey[] {
    return Array.from(this.loaders.entries())
      .filter(([_, loader]) => !loader.useGenericApi())
      .map(([key, _]) => key)
  }
}
